import React from 'react'
import { Link } from 'react-router-dom'
import { OfferingLink } from './MainHeader'
import { collectionPath } from '../constants/shopCategories'
import { SITE_LINKS } from '../constants/siteLinks'
import { HOME_OFFERINGS } from '../constants/homeOfferings'
import './HomeHighlights.css'

/** Shared section heading: chip, title with a gradient accent word, intro, optional action on the right. */
export function SectionHead({ chip, title, accent, intro, action, center = false }) {
  return (
    <header className={`hh-head${center ? ' is-center' : ''}`} data-reveal>
      <div>
        <span className="fab-chip">{chip}</span>
        <h2 className="fab-title">
          {title} <span className="fab-gradient-text">{accent}</span>
        </h2>
        {intro && <p className="fab-intro">{intro}</p>}
      </div>
      {action}
    </header>
  )
}

// Product categories only (services such as software and training have their own section).
// First id is the large featured tile; the rest and "View all products" form a 2 × 2 grid beside it.
const CATEGORY_IDS = ['starlink', 'cctv', 'power', 'networking']

/** Label, one-line description and image per category tile (image falls back to the offering's). */
const CATEGORY_DETAILS = {
  starlink: {
    label: 'Fiber & Starlink Internet',
    text: 'Fast, reliable internet for homes, businesses, schools and remote sites — supplied, installed and supported by our team.',
    image: '/assets/img/slider/starlink-daylight-4k.jpg',
    cta: 'Shop Starlink',
  },
  cctv: { label: 'Digital Security', text: 'CCTV, recorders, alarms & intercoms', image: '/assets/img/home/category-digital-security.jpg' },
  power: { label: 'Power Backup & UPS', text: 'Keep your equipment running', image: '/assets/img/home/category-power-backup.jpg' },
  networking: { label: 'Networking & IT', text: 'Switches, Wi-Fi & cabling', image: '/assets/img/home/category-networking-it.jpg' },
}

/** "Explore Our Product Categories": featured category, three category tiles and a link to the shop. */
export function ProductCategories({ onOpenService }) {
  const items = CATEGORY_IDS.map((id) => HOME_OFFERINGS.find((item) => item.id === id)).filter(Boolean)
  const [featured, ...rest] = items
  const detail = (item) => CATEGORY_DETAILS[item.id] || {}

  return (
    <section className="hh-section" aria-labelledby="hh-categories-title">
      <div className="hh-container">
        <SectionHead
          chip="Shop by category"
          title="Explore Our Product"
          accent="Categories"
          intro="Find the right products and solutions for your home, office or business."
        />
        <div className="hh-catgrid" id="hh-categories-title">
          {featured && (
            <OfferingLink item={featured} onOpenService={onOpenService} className="hh-feature" data-reveal="left" data-tilt="3">
              <span className="hh-feature__img" style={{ backgroundImage: `url('${detail(featured).image || featured.image}')` }} />
              <span className="hh-feature__body">
                <span className="hh-feature__tag">
                  <i className="fa-solid fa-star" aria-hidden="true" />
                  Most popular
                </span>
                <strong>{detail(featured).label || featured.name}</strong>
                <span className="hh-feature__text">{detail(featured).text}</span>
                <span className="hh-feature__cta">
                  {detail(featured).cta || 'Explore'}
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </span>
              </span>
            </OfferingLink>
          )}

          {rest.map((item, index) => (
            <OfferingLink key={item.id} item={item} onOpenService={onOpenService} className="hh-tile" data-reveal="zoom" data-tilt="5" style={{ '--i': index + 1 }}>
              <span className="hh-tile__img">
                <img src={detail(item).image || item.image} alt="" loading="lazy" />
              </span>
              <span className="hh-tile__body">
                <span>
                  <strong>{detail(item).label || item.name}</strong>
                  <span className="hh-tile__text">{detail(item).text}</span>
                </span>
                <span className="hh-tile__go" aria-hidden="true">
                  <i className="fa-solid fa-arrow-right" />
                </span>
              </span>
            </OfferingLink>
          ))}

          <Link to={SITE_LINKS.shop} className="hh-tile hh-tile--all" data-reveal="zoom" data-tilt="5" style={{ '--i': rest.length + 1 }}>
            <span className="hh-tile__all-icon" aria-hidden="true">
              <i className="fa-solid fa-store" />
            </span>
            <strong>View all products</strong>
            <span className="hh-tile__text">Browse the full Fabritech store</span>
            <span className="hh-tile__all-cta">
              Browse Shop
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  )
}

const SERVICE_ICONS = {
  'network-infrastructure': 'fa-network-wired',
  'digital-security': 'fa-shield-halved',
  'starlink-installation': 'fa-satellite-dish',
  'software-development': 'fa-code',
  'canalplus-dstv': 'fa-tv',
  'internships-courses': 'fa-graduation-cap',
}

const pad = (n) => String(n).padStart(2, '0')

// Mosaic slot per position: the first card is large, the others alternate square / wide
const MOSAIC_SLOTS = ['is-large', 'is-square', 'is-square', 'is-wide', 'is-wide', 'is-wide']

/**
 * "Complete IT Solutions for Your Business": the six services as a photo mosaic. Each card is the service
 * photo with a brand-blue fade, its name and summary; hovering reveals key features. Clicking opens it.
 */
export function ServicesGrid({ services, onLearnMore }) {
  return (
    <section id="services" className="hh-section hh-section--tint" aria-labelledby="hh-services-title">
      <div className="hh-container">
        <SectionHead
          chip="Our services"
          title="Complete IT Solutions for"
          accent="Your Business"
          intro="From internet connectivity to software development, we provide end-to-end solutions that keep your organization connected, secure and productive."
          action={
            <a href={SITE_LINKS.contact} className="hh-btn hh-btn--primary">
              Get a Free Consultation
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </a>
          }
        />
        <ul className="hh-mosaic" id="hh-services-title">
          {services.map((service, i) => (
            <li key={service.id} className={MOSAIC_SLOTS[i] || 'is-square'} data-reveal="zoom" style={{ '--i': i }}>
              <button type="button" className="hh-mcard" onClick={() => onLearnMore(service)} data-tilt="3">
                <span className="hh-mcard__img" style={{ backgroundImage: `url('/${service.image.replace(/^\//, '')}')` }} />
                <span className="hh-mcard__top">
                  <span className="hh-mcard__icon" aria-hidden="true">
                    <i className={`fa-solid ${SERVICE_ICONS[service.id] || 'fa-gear'}`} />
                  </span>
                  <span className="hh-mcard__num">{pad(i + 1)}</span>
                </span>
                <span className="hh-mcard__body">
                  <span className="hh-mcard__title">{service.title}</span>
                  <span className="hh-mcard__text">{service.shortDescription}</span>
                  <span className="hh-mcard__more">
                    {service.features.slice(0, 3).map((feature) => (
                      <span key={feature} className="hh-mcard__feature">
                        <i className="fa-solid fa-check" aria-hidden="true" />
                        {feature}
                      </span>
                    ))}
                  </span>
                  <span className="hh-mcard__cta">
                    Learn more
                    <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

const REASONS = [
  { icon: 'fa-certificate', title: 'Genuine Products', text: 'Original equipment from trusted brands, with warranty.' },
  { icon: 'fa-truck-fast', title: 'Nationwide Delivery', text: 'Fast, reliable dispatch to anywhere in Rwanda.' },
  { icon: 'fa-screwdriver-wrench', title: 'Expert Installation', text: 'Set up and configured by Fabritech engineers.' },
  { icon: 'fa-mobile-screen', title: 'Easy Payments', text: 'Pay with MTN MoMo, Airtel Money or card.' },
  { icon: 'fa-headset', title: 'Local Support', text: 'A Kigali-based team that answers when you call.' },
  { icon: 'fa-graduation-cap', title: 'Professional Training', text: 'Practical courses for individuals and businesses.' },
]

/** "Your Trusted Technology Partner": intro and photo on the left, the six reasons on the right. */
export function WhyChoose() {
  return (
    <section className="hh-section hh-section--tint" aria-labelledby="hh-why-title">
      <div className="hh-container hh-why">
        <div className="hh-why__intro" data-reveal="left">
          <span className="fab-chip">Why choose Fabritech</span>
          <h2 id="hh-why-title" className="fab-title">
            Your Trusted Technology <span className="fab-gradient-text">Partner</span>
          </h2>
          <p className="fab-intro">
            From the first consultation to installation and after-sales support, one local team takes care of your
            connectivity, security and IT — so everything simply works.
          </p>
          <div className="hh-why__photo" data-tilt="4">
            <img src="/assets/img/home/why-team.jpg" alt="Fabritech team working together on network and satellite installations" loading="lazy" />
            <span className="hh-why__badge fx-float">
              <span className="hh-why__badge-icon" aria-hidden="true">
                <i className="fa-solid fa-location-dot" />
              </span>
              <span>
                <strong>Based in Kigali</strong>
                Serving clients across Rwanda
              </span>
            </span>
          </div>
        </div>

        <div className="hh-why__side">
          <ul className="hh-why__list">
            {REASONS.map((reason, index) => (
              <li key={reason.title} data-reveal="right" style={{ '--i': index }}>
                <span className="hh-why__icon" aria-hidden="true">
                  <i className={`fa-solid ${reason.icon}`} />
                </span>
                <span>
                  <strong>{reason.title}</strong>
                  <span className="hh-why__text">{reason.text}</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="hh-why__cta" data-reveal>
            <a href={SITE_LINKS.contact} className="hh-btn hh-btn--primary">
              Talk to Our Team
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </a>
            <a href="tel:+250788601280" className="hh-why__call">
              <i className="fa-solid fa-phone" aria-hidden="true" />
              +250 788 601 280
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

const SOLUTIONS = [
  {
    id: 'internet',
    icon: 'fa-wifi',
    image: '/assets/img/slider/starlink-daylight-4k.jpg',
    kicker: 'Connectivity',
    title: 'Stay Connected Anywhere',
    text: 'Fiber and Starlink internet for homes, businesses, schools and remote sites.',
    tags: ['Starlink kits', 'Fiber internet', 'Wi-Fi setup'],
    cta: 'Explore Starlink',
    to: collectionPath('starlink'),
  },
  {
    id: 'security',
    icon: 'fa-shield-halved',
    image: '/assets/img/home/solution-security.jpg',
    kicker: 'Digital Security',
    title: 'Protect What Matters',
    text: 'CCTV, access control, intercoms and alarm systems for every kind of site.',
    tags: ['CCTV', 'Access control', 'Alarms'],
    cta: 'Explore Security',
    to: collectionPath('digital-security'),
  },
  {
    id: 'network',
    icon: 'fa-network-wired',
    image: '/assets/img/service/network-cards/structured-cabling.jpg',
    kicker: 'Network Solutions',
    title: 'Build Reliable Networks',
    text: 'Switching, Wi-Fi and structured cabling for offices, schools and enterprises.',
    tags: ['Switches', 'Access points', 'Cabling'],
    cta: 'Explore Networking',
    to: SITE_LINKS.serviceNetwork,
  },
]

/** "Technology for a Better Tomorrow": three tall photo cards with a frosted info panel. */
export function Solutions() {
  return (
    <section className="hh-section" aria-labelledby="hh-solutions-title">
      <div className="hh-container">
        <SectionHead
          chip="Our solutions"
          title="Technology for a Better"
          accent="Tomorrow"
          intro="Explore our key solutions designed for homes, businesses, schools and institutions."
        />
        <div className="hh-sols" id="hh-solutions-title">
          {SOLUTIONS.map((item, index) => (
            <Link key={item.id} to={item.to} className="hh-sol" data-reveal style={{ '--i': index }} data-tilt="4">
              <span className="hh-sol__img" style={{ backgroundImage: `url('${item.image}')` }} />
              <span className="hh-sol__icon" aria-hidden="true">
                <i className={`fa-solid ${item.icon}`} />
              </span>
              <span className="hh-sol__panel">
                <span className="hh-sol__kicker">{item.kicker}</span>
                <span className="hh-sol__title">{item.title}</span>
                <span className="hh-sol__text">{item.text}</span>
                <span className="hh-sol__tags">
                  {item.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </span>
                <span className="hh-sol__cta">
                  {item.cta}
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
